import { PaymentDirection, PaymentMethodBadgeColor, PaymentMethodPreset, } from '@cpce/database/enums';
import z from 'zod';
const financialAccountNameResolutionInclude = {
    supplierPayable: { select: { name: true } },
};
export const financialAccountWithRelations = {
    include: {
        subFinancialAccounts: {
            where: { deletedAt: null },
            include: {
                associatedJournal: true,
                bank: true,
                parentFinancialAccount: true,
                ...financialAccountNameResolutionInclude,
                subFinancialAccounts: {
                    where: { deletedAt: null },
                    include: {
                        associatedJournal: true,
                        parentFinancialAccount: true,
                        ...financialAccountNameResolutionInclude,
                    },
                },
            },
        },
        associatedJournal: true,
        bank: true,
        parentFinancialAccount: true,
        ...financialAccountNameResolutionInclude,
    },
};
export const FinancialAccountValues = {
    passive: 'passive',
    active: 'active',
    charge: 'charge',
    product: 'product',
};
const financialAccountTypeEnumSchema = z.enum(Object.values(FinancialAccountValues), {
    message: 'Le type de compte financier est requis',
});
export const FinancialAccountTypeSchema = z.preprocess((val) => (typeof val === 'string' ? val.trim().toLowerCase() : val), financialAccountTypeEnumSchema);
// Capabilities of a "mode de règlement". They drive the behaviours that depend on the nature of
// the instrument and cannot be inferred from an account's name: cheque numbering, échéancier,
// pièces/billets breakdown, SEPA transfer file. Never written directly — a preset writes them as a
// bundle — but read one by one by every posting, remise and échéancier path.
export const PAYMENT_METHOD_CAPABILITIES = [
    'hasInstrumentNumber',
    'supportsEcheancier',
    'hasCashBreakdown',
    'producesTransferFile',
];
// Direction(s) the mode serves: inflow modes are transit accounts emptied by the remise. Outflow
// modes are either the 512 itself (an issued cheque or card payment credits the bank at émission)
// or the « virements à émettre » transit account, emptied by the remise de virements. Each picker
// filters on its own direction.
export const PAYMENT_METHOD_DIRECTIONS = ['usableForInflow', 'usableForOutflow'];
const noFlags = {
    hasInstrumentNumber: false,
    supportsEcheancier: false,
    hasCashBreakdown: false,
    producesTransferFile: false,
    usableForInflow: false,
    usableForOutflow: false,
};
// The list of modes de règlement the client validated (2026-09-01). Encaissements name their
// instrument because each one has its own transit account. Décaissements mostly do not, since an
// issued cheque, virement and CB all credit the same 512 — there the instrument is carried by the
// payment (`SUPPLIER_PAYMENT_INSTRUMENTS`). The exception is the batched virement (2026-10-14): it
// waits on its own transit account until the remise de virements sends it to the bank as a SEPA
// file.
//
// Badge colours are part of the preset rather than allocated on the fly: they used to be, because
// an account label is free text and could not be trusted to stay stable. A preset can. The values
// reproduce what the seed already assigned, so no badge already on screen changes.
export const PAYMENT_METHOD_PRESETS = {
    [PaymentMethodPreset.chequeEncaisse]: {
        label: 'Encaissement — Chèques',
        description: 'Compte de transit des chèques reçus, vidé par la remise en banque. Chaque règlement porte un numéro et peut être découpé en échéances.',
        badgeColor: PaymentMethodBadgeColor.blue,
        flags: {
            ...noFlags,
            usableForInflow: true,
            hasInstrumentNumber: true,
            supportsEcheancier: true,
        },
    },
    [PaymentMethodPreset.carteEncaissee]: {
        label: 'Encaissement — Carte bancaire',
        description: 'Compte de transit des paiements par carte, vidé par la remise en banque.',
        badgeColor: PaymentMethodBadgeColor.purple,
        flags: { ...noFlags, usableForInflow: true },
    },
    [PaymentMethodPreset.especes]: {
        label: 'Encaissement — Espèces',
        description: 'La caisse joue le rôle de compte de transit. La remise en banque demande le détail des pièces et billets déposés.',
        badgeColor: PaymentMethodBadgeColor.green,
        flags: { ...noFlags, usableForInflow: true, hasCashBreakdown: true },
    },
    [PaymentMethodPreset.decaissementBancaire]: {
        label: 'Décaissement — Compte bancaire',
        description: 'Compte crédité par les paiements émis (fournisseurs). Le moyen de paiement — chèque, virement, carte — est choisi règlement par règlement.',
        badgeColor: PaymentMethodBadgeColor.gray,
        flags: { ...noFlags, usableForOutflow: true },
    },
    [PaymentMethodPreset.virementAEmettre]: {
        label: 'Décaissement — Virements à émettre',
        description: 'Compte de transit des virements émis (fournisseurs, créances). La remise de virements le vide vers la banque et produit le fichier SEPA à déposer sur le site de la banque.',
        badgeColor: PaymentMethodBadgeColor.orange,
        flags: { ...noFlags, usableForOutflow: true, producesTransferFile: true },
    },
};
export const PAYMENT_METHOD_PRESET_VALUES = Object.values(PaymentMethodPreset);
export function resolvePaymentMethodPresetFlags(preset) {
    return preset ? PAYMENT_METHOD_PRESETS[preset].flags : { ...noFlags, usableForInflow: true };
}
// The account columns a preset expands into. The preset is the only thing a client or an import
// names; every other payment-method column is derived from it here, so no writer can build a
// combination no preset expresses.
export function expandPaymentMethodPreset(preset) {
    return {
        paymentMethodPreset: preset,
        isPaymentMethod: preset !== null,
        badgeColor: preset ? PAYMENT_METHOD_PRESETS[preset].badgeColor : null,
        ...resolvePaymentMethodPresetFlags(preset),
    };
}
// A « virements à émettre » account only ever holds virements: its remise turns them into a SEPA
// file, where a cheque or card payment has nothing to be.
export const transferAccountInstrumentError = (code) => `Le compte ${code} est un compte de virements à émettre : seul un virement peut y être enregistré.`;
export const financialAccountDataSchema = z.object({
    code: z.string().min(1, 'Le code est requis'),
    name: z.string().min(1, 'Le nom est requis').nullable(),
    accountType: FinancialAccountTypeSchema,
    associatedJournalId: z.number().int().positive().nullable().optional(),
    parentFinancialAccountId: z.number().int().positive().nullable().optional(),
    // Declares a treasury account (classe 5) a "mode de règlement" and configures it in one go: the
    // capability and direction booleans are written from the preset, never sent by the client. `null`
    // clears the flag. Omitted leaves the account's payment-method configuration untouched, so
    // renaming an account or attaching a journal cannot silently reset it.
    paymentMethodPreset: z.enum(PaymentMethodPreset).nullable().optional(),
    // The real bank account (IBAN) behind a 512: a bank deposit can be made to it. One bank per
    // financial account, enforced by a unique index — otherwise the deposit destination is
    // ambiguous.
    bankId: z.number().int().positive().nullable().optional(),
});
// The payment-method picker is direction-aware: a salarié règlement offers inflow modes, a
// supplier payment offers outflow modes. Defaults to inflow, the pre-existing behaviour.
export const paymentMethodAccountsFiltersSchema = z.object({
    direction: z.enum(PaymentDirection).default(PaymentDirection.inflow),
});
export const financialAccountsFiltersSchema = z.object({
    codeStartsWith: z.string().optional(),
    // Accept both an explicit JS `null` (in-process callers) and the wire forms axios produces
    // for `null` (`?parentFinancialAccountId=`) or an explicit `null` literal — all map to
    // "filter for accounts with no parent" (i.e. root-level only).
    parentFinancialAccountId: z
        .preprocess((val) => (val === '' || val === 'null' ? null : val), z.union([z.coerce.number().int().positive(), z.null()]))
        .optional(),
});
export function resolveFinancialAccountName(account) {
    const SUPPLIER_PREFIX = 'Compte fournisseur - ';
    const UNNAMED_ACCOUNT = 'Compte sans nom';
    if (account.name)
        return account.name;
    if (account.supplierPayable) {
        return `${SUPPLIER_PREFIX}${account.supplierPayable.name}`;
    }
    return UNNAMED_ACCOUNT;
}
export const formatAccountLabel = (account) => account.name ? `${account.code} - ${account.name}` : account.code;
// Payload to relink a salarié's 411 or a supplier's 401: connect an existing unlinked
// sub-account, create one with an editable suggested code, or (a supplier only) clear it.
// `moveEntries` also moves the tiers' écritures onto it, except the locked ones. An object rather
// than a discriminated union so `createZodDto` can build a DTO class from it.
export const setTiersAccountSchema = z.discriminatedUnion('mode', [
    z.object({
        mode: z.literal('connect'),
        financialAccountId: z.number({ error: 'Le compte à connecter est requis' }).int().positive(),
        moveEntries: z.boolean().default(false),
    }),
    z.object({
        mode: z.literal('create'),
        code: z
            .string({ error: 'Le code du compte est requis' })
            .min(1, 'Le code du compte est requis'),
        name: z.string({ error: 'Le nom du compte est requis' }).min(1, 'Le nom du compte est requis'),
        moveEntries: z.boolean().default(false),
    }),
    z.object({ mode: z.literal('clear'), moveEntries: z.boolean().default(false) }),
]);
